// ─────────────────────────────────────────────────────────────────────────────
// Process Manufacturing — Quality Control Page
// Lab testing dashboard (density, sulfur, flash point, octane), pass/quarantine/regrade workflow
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { Plus, Search, Trash2, Microscope, CheckCircle, XCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import { useApi } from '@/shared/hooks/useApi';

interface QualitySample {
  _id: string;
  sampleNumber: string;
  batchRecordId: string;
  orderId: string;
  samplePoint: string;
  samplePointRef: string;
  productId: string;
  tankId: string;
  batchNumber: string;
  sampledBy: string;
  sampledAt: string;
  testedBy: string;
  testedAt: string;
  results: Array<{
    parameterCode: string;
    parameterName: string;
    unit: string;
    testMethod: string;
    value: number;
    minSpec: number;
    maxSpec: number;
    isWithinSpec: boolean;
    isCritical: boolean;
  }>;
  status: string;
  disposition: string;
  regradeToProductId: string;
  notes: string;
}

export function QualityControlPage() {
  const [samples, setSamples] = useState<QualitySample[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSamplePoint, setFilterSamplePoint] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [viewSample, setViewSample] = useState<QualitySample | null>(null);
  const { get, post } = useApi();
  
  useEffect(() => {
    fetchSamples();
  }, [get]);
  
  async function fetchSamples() {
    setLoading(true);
    try {
      const res = await get('/api/manufacturing/quality/samples');
      setSamples(res.samples || []);
    } catch (error) {
      console.error('Failed to fetch samples:', error);
    } finally {
      setLoading(false);
    }
  }
  
  function handleOpenCreate() {
    setFormData({
      sampleNumber: '',
      batchRecordId: '',
      orderId: '',
      samplePoint: 'tank_receipt',
      samplePointRef: '',
      productId: '',
      tankId: '',
      batchNumber: '',
      sampledBy: 'current-user',
      sampledAt: new Date().toISOString(),
      results: [],
      status: 'quarantine',
    });
    setShowDialog(true);
  }
  
  function handleView(sample: QualitySample) {
    setViewSample(sample);
  }

  // Samples are draw-then-decide (see handleDecision) — there is no
  // supported way to edit or delete an already-drawn sample record, so
  // this only ever creates a new one.
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await post('/api/manufacturing/quality/sample', formData);
      setShowDialog(false);
      fetchSamples();
    } catch (error) {
      console.error('Failed to save sample:', error);
    }
  }
  
  async function handleDecision(sampleId: string, status: 'passed' | 'failed' | 'regraded', regradeToProductId?: string) {
    try {
      await post('/api/manufacturing/quality/decide', { sampleId, status, disposition: status === 'passed' ? 'accept' : status === 'failed' ? 'reject' : 'regrade', regradeToProductId });
      fetchSamples();
    } catch (error) {
      console.error('Failed to record decision:', error);
    }
  }
  
  function getOverallStatus(sample: QualitySample): 'pass' | 'fail' | 'partial' {
    if (!sample.results.length) return 'partial';
    const criticalFail = sample.results.some(r => r.isCritical && !r.isWithinSpec);
    const anyFail = sample.results.some(r => !r.isWithinSpec);
    if (criticalFail) return 'fail';
    if (anyFail) return 'partial';
    return 'pass';
  }
  
  const filteredSamples = samples.filter(s => 
    s.sampleNumber.toLowerCase().includes(search.toLowerCase()) ||
    s.batchNumber.toLowerCase().includes(search.toLowerCase())
  ).filter(s => !filterStatus || s.status === filterStatus)
  .filter(s => !filterSamplePoint || s.samplePoint === filterSamplePoint);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Quality & Lab Control</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Lab testing dashboard (density, sulfur, flash point, octane), pass/quarantine/regrade workflow</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Sample
        </Button>
      </div>
      
      <div className="flex flex-wrap gap-4 mb-4">
        <Input placeholder="Search samples..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Status</SelectItem>
            <SelectItem value="quarantine">Quarantine</SelectItem>
            <SelectItem value="testing">Testing</SelectItem>
            <SelectItem value="passed">Passed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="regraded">Regraded</SelectItem>
            <SelectItem value="released">Released</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSamplePoint} onValueChange={setFilterSamplePoint}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All Sample Points" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Sample Points</SelectItem>
            <SelectItem value="tank_receipt">Tank Receipt</SelectItem>
            <SelectItem value="pre_process">Pre-Process</SelectItem>
            <SelectItem value="during_process">During Process</SelectItem>
            <SelectItem value="post_process">Post-Process</SelectItem>
            <SelectItem value="tank_dispatch">Tank Dispatch</SelectItem>
            <SelectItem value="packaging">Packaging</SelectItem>
            <SelectItem value="customer_delivery">Customer Delivery</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {loading ? (
        <Card className="p-8 text-center">Loading...</Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sample #</TableHead>
                <TableHead>Batch #</TableHead>
                <TableHead>Sample Point</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Tank</TableHead>
                <TableHead>Sampled</TableHead>
                <TableHead>Tested</TableHead>
                <TableHead>Results</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Disposition</TableHead>
                <TableHead className="w-56">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSamples.map(sample => {
                const overall = getOverallStatus(sample);
                return (
                  <TableRow key={sample._id}>
                    <TableCell className="font-mono font-medium">{sample.sampleNumber}</TableCell>
                    <TableCell className="font-mono text-sm">{sample.batchNumber}</TableCell>
                    <TableCell><Badge variant="secondary">{sample.samplePoint.replace('_', ' ')}</Badge></TableCell>
                    <TableCell>{sample.productId}</TableCell>
                    <TableCell className="font-mono text-sm">{sample.tankId || sample.samplePointRef || '-'}</TableCell>
                    <TableCell className="text-sm">{sample.sampledAt ? new Date(sample.sampledAt).toLocaleString() : '-'}</TableCell>
                    <TableCell className="text-sm">{sample.testedAt ? new Date(sample.testedAt).toLocaleString() : 'Pending'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {sample.results.map((r, idx) => (
                          <span key={idx} className={`px-2 py-1 rounded text-xs font-medium ${r.isWithinSpec ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : r.isCritical ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'}`}>
                            {r.parameterCode}: {r.value} {r.unit}
                            {r.isCritical && !r.isWithinSpec && <span className="ml-1">⚠</span>}
                          </span>
                        ))}
                        {sample.results.length === 0 && <span className="text-gray-500 text-xs">No results</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={overall === 'pass' ? 'success' : overall === 'fail' ? 'destructive' : 'warning'}>
                        {overall === 'pass' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {overall === 'fail' && <XCircle className="w-3 h-3 mr-1" />}
                        {overall === 'partial' && <AlertTriangle className="w-3 h-3 mr-1" />}
                        {overall.charAt(0).toUpperCase() + overall.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={sample.disposition === 'accept' ? 'success' : sample.disposition === 'reject' ? 'destructive' : 'secondary'}>
                        {sample.disposition}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleView(sample)} title="View Details">
                          <Microscope className="w-4 h-4" />
                        </Button>
                        {sample.status === 'quarantine' || sample.status === 'testing' ? (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleDecision(sample._id, 'passed')} title="Pass" className="text-green-500">
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDecision(sample._id, 'failed')} title="Fail" className="text-red-500">
                              <XCircle className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDecision(sample._id, 'regraded')} title="Regrade" className="text-blue-500">
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredSamples.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-gray-500">No quality samples found. Click "Create Sample" to get started.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
      
      {/* View Sample Dialog */}
      <Dialog open={!!viewSample} onOpenChange={open => !open && setViewSample(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sample Details: {viewSample?.sampleNumber}</DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><span className="text-gray-500">Batch #:</span> <span className="font-mono ml-2">{viewSample?.batchNumber}</span></div>
              <div><span className="text-gray-500">Sample Point:</span> <span className="ml-2">{viewSample?.samplePoint}</span></div>
              <div><span className="text-gray-500">Product:</span> <span className="ml-2">{viewSample?.productId}</span></div>
              <div><span className="text-gray-500">Tank/Ref:</span> <span className="ml-2">{viewSample?.tankId || viewSample?.samplePointRef}</span></div>
              <div><span className="text-gray-500">Sampled By:</span> <span className="ml-2">{viewSample?.sampledBy}</span></div>
              <div><span className="text-gray-500">Sampled At:</span> <span className="ml-2">{viewSample?.sampledAt ? new Date(viewSample.sampledAt).toLocaleString() : '-'}</span></div>
              <div><span className="text-gray-500">Tested By:</span> <span className="ml-2">{viewSample?.testedBy || 'Pending'}</span></div>
              <div><span className="text-gray-500">Tested At:</span> <span className="ml-2">{viewSample?.testedAt ? new Date(viewSample.testedAt).toLocaleString() : 'Pending'}</span></div>
              <div><span className="text-gray-500">Status:</span> <Badge variant={viewSample?.status === 'passed' ? 'success' : viewSample?.status === 'failed' ? 'destructive' : 'secondary'} className="ml-2">{viewSample?.status}</Badge></div>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Test Results</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Parameter</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Min Spec</TableHead>
                    <TableHead>Max Spec</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Critical</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewSample?.results.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{r.parameterName}</TableCell>
                      <TableCell className="font-mono">{r.value}</TableCell>
                      <TableCell>{r.unit}</TableCell>
                      <TableCell>{r.minSpec !== null ? r.minSpec : '-'}</TableCell>
                      <TableCell>{r.maxSpec !== null ? r.maxSpec : '-'}</TableCell>
                      <TableCell>{r.testMethod}</TableCell>
                      <TableCell>{r.isCritical ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Badge variant={r.isWithinSpec ? 'success' : r.isCritical ? 'destructive' : 'warning'}>
                          {r.isWithinSpec ? 'Pass' : 'Fail'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {viewSample?.notes && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded">
                <h4 className="font-medium mb-1">Notes</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">{viewSample.notes}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewSample(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Quality Sample</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Sample Number *</label>
                  <Input value={formData.sampleNumber} onChange={e => setFormData({...formData, sampleNumber: e.target.value.toUpperCase()})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Batch Record ID *</label>
                  <Input value={formData.batchRecordId} onChange={e => setFormData({...formData, batchRecordId: e.target.value})} required placeholder="Batch Record ObjectId" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Order ID *</label>
                  <Input value={formData.orderId} onChange={e => setFormData({...formData, orderId: e.target.value})} required placeholder="Order ObjectId" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sample Point *</label>
                  <Select value={formData.samplePoint} onValueChange={v => setFormData({...formData, samplePoint: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tank_receipt">Tank Receipt</SelectItem>
                      <SelectItem value="pre_process">Pre-Process</SelectItem>
                      <SelectItem value="during_process">During Process</SelectItem>
                      <SelectItem value="post_process">Post-Process</SelectItem>
                      <SelectItem value="tank_dispatch">Tank Dispatch</SelectItem>
                      <SelectItem value="packaging">Packaging</SelectItem>
                      <SelectItem value="customer_delivery">Customer Delivery</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Product ID *</label>
                  <Input value={formData.productId} onChange={e => setFormData({...formData, productId: e.target.value})} required placeholder="Product ObjectId" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tank ID</label>
                  <Input value={formData.tankId} onChange={e => setFormData({...formData, tankId: e.target.value})} placeholder="Tank ObjectId" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Batch Number</label>
                  <Input value={formData.batchNumber} onChange={e => setFormData({...formData, batchNumber: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Sample Point Ref</label>
                  <Input value={formData.samplePointRef} onChange={e => setFormData({...formData, samplePointRef: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quarantine">Quarantine</SelectItem>
                      <SelectItem value="testing">Testing</SelectItem>
                      <SelectItem value="passed">Passed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="regraded">Regraded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Results Section */}
              <div>
                <label className="block text-sm font-medium mb-2">Test Results</label>
                <div className="space-y-2">
                  {formData.results?.map((result: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded flex-wrap">
                      <Input placeholder="Param Code" value={result.parameterCode} onChange={e => { const newResults = [...formData.results]; newResults[idx] = {...newResults[idx], parameterCode: e.target.value}; setFormData({...formData, results: newResults}); }} className="w-28" />
                      <Input placeholder="Param Name" value={result.parameterName} onChange={e => { const newResults = [...formData.results]; newResults[idx] = {...newResults[idx], parameterName: e.target.value}; setFormData({...formData, results: newResults}); }} className="w-40" />
                      <Input type="number" placeholder="Value" value={result.value} onChange={e => { const newResults = [...formData.results]; newResults[idx] = {...newResults[idx], value: Number(e.target.value)}; setFormData({...formData, results: newResults}); }} className="w-24" />
                      <Input placeholder="Unit" value={result.unit} onChange={e => { const newResults = [...formData.results]; newResults[idx] = {...newResults[idx], unit: e.target.value}; setFormData({...formData, results: newResults}); }} className="w-20" />
                      <Input type="number" placeholder="Min Spec" value={result.minSpec} onChange={e => { const newResults = [...formData.results]; newResults[idx] = {...newResults[idx], minSpec: e.target.value ? Number(e.target.value) : null}; setFormData({...formData, results: newResults}); }} className="w-24" />
                      <Input type="number" placeholder="Max Spec" value={result.maxSpec} onChange={e => { const newResults = [...formData.results]; newResults[idx] = {...newResults[idx], maxSpec: e.target.value ? Number(e.target.value) : null}; setFormData({...formData, results: newResults}); }} className="w-24" />
                      <Input placeholder="Method" value={result.testMethod} onChange={e => { const newResults = [...formData.results]; newResults[idx] = {...newResults[idx], testMethod: e.target.value}; setFormData({...formData, results: newResults}); }} className="w-32" />
                      <label className="flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={result.isCritical} onChange={e => { const newResults = [...formData.results]; newResults[idx] = {...newResults[idx], isCritical: e.target.checked}; setFormData({...formData, results: newResults}); }} />
                        Critical
                      </label>
                      <Button variant="ghost" size="icon" onClick={() => { const newResults = [...formData.results]; newResults.splice(idx, 1); setFormData({...formData, results: newResults}); }}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setFormData({...formData, results: [...(formData.results || []), { parameterCode: '', parameterName: '', unit: '', testMethod: '', value: 0, minSpec: null, maxSpec: null, isCritical: false, isWithinSpec: true }]})}>
                    <Plus className="w-4 h-4 mr-1" /> Add Result
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}